import { Badge } from 'src/design/atoms/Badge';
import { ExternalLinkActionIcon } from 'src/design/atoms/ExternalLinkActionIcon';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { formatDate } from 'src/utils';
import {
  inspectionStateColor,
  inspectionStateLabel,
} from 'src/app/inspections/inspectionStates';
import {
  InspectionOverviewDto,
  InspectionState,
} from 'src/api/queries/inspections/useInspectionsOverview';
import { useTableExports } from 'src/design/organisms/useTableExports';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';

interface IInspectionsOverviewTableProps {
  inspections: InspectionOverviewDto[];
  loading?: boolean;
  fetching?: boolean;
  onRefresh?: () => void;
}

const isOverdue = (inspection: InspectionOverviewDto): boolean => {
  if (inspection.state === InspectionState.Completed) return false;
  if (!inspection.dueAt) return false;
  return new Date(inspection.dueAt).getTime() < Date.now();
};

export const InspectionsOverviewTable: React.FC<
  IInspectionsOverviewTableProps
> = ({ inspections, loading = true, fetching = false, onRefresh }) => {
  const columns = useMemo<MRT_ColumnDef<InspectionOverviewDto>[]>(
    () => [
      {
        id: 'site',
        accessorFn: (row) => `${row.siteName} ${row.siteNumber ?? ''}`.trim(),
        header: 'Site',
        size: 240,
        Cell: ({ row }) => (
          <Stack gap={0}>
            <Text c="black" fw={500}>
              {row.original.siteName}
            </Text>
            {row.original.siteNumber && (
              <Text c="black" size="sm">
                {row.original.siteNumber}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        id: 'client',
        header: 'Client',
        size: 180,
        accessorFn: (row) => row.clientName ?? '',
      },
      {
        id: 'type',
        header: 'Type',
        size: 180,
        accessorFn: (row) => row.inspectionTypeName,
      },
      {
        id: 'status',
        header: 'Status',
        size: 140,
        accessorFn: (row) => inspectionStateLabel(row.state),
        Cell: ({ row }) => (
          <Badge
            color={inspectionStateColor(row.original.state)}
            variant="filled"
          >
            {inspectionStateLabel(row.original.state).toUpperCase()}
          </Badge>
        ),
      },
      {
        id: 'scheduled',
        header: 'Scheduled',
        size: 130,
        accessorFn: (row) => formatDate(row.scheduledStartAt),
      },
      {
        id: 'due',
        header: 'Due',
        size: 130,
        accessorFn: (row) => formatDate(row.dueAt),
        Cell: ({ row }) => (
          <Text c={isOverdue(row.original) ? 'red' : 'black'}>
            {formatDate(row.original.dueAt) || '--'}
          </Text>
        ),
      },
      {
        id: 'progress',
        header: 'Progress',
        size: 110,
        accessorFn: (row) =>
          `${row.terminalDeviceCount} / ${row.scopedDeviceCount}`,
        Cell: ({ row }) => (
          <Text c="black">
            {row.original.terminalDeviceCount} /{' '}
            {row.original.scopedDeviceCount}
          </Text>
        ),
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'inspections-overview-table',
    columns: columns,
    defaultSorting: [{ id: 'due', desc: false }],
  });

  const toRow = (item: InspectionOverviewDto) => ({
    ['Site']: item.siteName,
    ['Site Number']: item.siteNumber ?? '',
    ['Client']: item.clientName ?? '',
    ['Type']: item.inspectionTypeName,
    ['Status']: inspectionStateLabel(item.state),
    ['Scheduled']: formatDate(item.scheduledStartAt),
    ['Due']: formatDate(item.dueAt),
    ['Progress']: `${item.terminalDeviceCount} / ${item.scopedDeviceCount}`,
  });

  const tableExports = useTableExports<InspectionOverviewDto>(
    'inspections',
    (data) => data.map(toRow),
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => (
        <Group>
          <ExternalLinkActionIcon
            href={row.original.performUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open in Proact"
          />
        </Group>
      ),
      enableClickToCopy: false,
      columns: columns,
      data: inspections,
    },
    getRowId: (row) => row.id,
    totalCount: inspections.length,
    isLoading: loading,
    isFetching: fetching,
    isError: false,
    onRefresh,
    ...tableExports,
  });

  return table;
};
